import FormControl from '@mui/material/FormControl'
import { InputBase } from '@mui/material'
import { Text, Flex, Button } from 'components/elements'
import { styled, CSS } from 'theme'
import { ChangeEvent, ReactNode, Ref, forwardRef, useState } from 'react'
import { FieldError } from 'react-hook-form'
import { Span } from 'theme/globalStyleCss'

type InputFieldType = 'number' | 'text' | 'password'

export interface InputFieldProps {
  name: string
  css?: CSS
  onChange?: (event: ChangeEvent<HTMLInputElement>) => void
  placeholder: string
  label?: string
  preLabel?: boolean
  error?: FieldError | string
  disabled?: boolean
  value?: string | null
  type?: InputFieldType
  textArea?: boolean
  iconElement?: {
    showIcon?: boolean
    iconPlacement?: string
    icon?: ReactNode
    toggleIcon?: ReactNode
    iconAsButton?: boolean
  }
  inputButton?: {
    end?: ReactNode
    start?: ReactNode
    css?: CSS
  }
  button?: {
    isEnabled: boolean
    buttonName: string
  }
  textAreaElement?: {
    row?: number
    col?: number
  }
  helperText?: {
    text?: string
    css?: CSS
  }
  maxLength?: number
  required?: boolean
}

export const InputField = forwardRef<HTMLInputElement, InputFieldProps>(
  (
    {
      name,
      css,
      type,
      onChange,
      placeholder,
      value,
      label,
      preLabel,
      error,
      textArea = false,
      disabled = false,
      iconElement = {
        showIcon: false,
        iconAsButton: false,
        iconPlacement: '',
        icon: '',
        toggleIcon: '',
        textHidden: false,
      },
      inputButton = { end: '', start: '' },
      button = { isEnabled: false, buttonName: '' },
      textAreaElement = { row: 3, col: '' },
      helperText,
      maxLength,
      required = false,
    },
    ref: Ref<HTMLInputElement>
  ) => {
    const { showIcon, iconPlacement, icon, toggleIcon, iconAsButton } =
      iconElement

    const { isEnabled: buttonEnabled, buttonName } = button
    const [inputType, setInputType] = useState(toggleIcon ? 'password' : type)
    const [showPassword, setShowPassword] = useState(toggleIcon)
    const [text, setText] = useState(value)

    const toggleText = () => {
      setInputType(prevInputType =>
        prevInputType === type ? 'password' : type
      )
      setShowPassword(prevShowPassword => !prevShowPassword)
    }

    // Handle input change with character limit
    const handleChange = (event: ChangeEvent<HTMLInputElement>) => {
      const { value } = event.target
      if (!maxLength || value.length <= maxLength) {
        setText(value)
        onChange?.(event)
      }
    }

    const currentIcon = toggleIcon ? (showPassword ? icon : toggleIcon) : icon

    return (
      <InputWrap
        variant="standard"
        css={{ ...InputWrapCSS(css), ...(preLabel ? PrelabelTextCss : {}) }}
        disabled={disabled}
      >
        {label && (
          <Text as="label" htmlFor={name} css={{ textTransform: 'capitalize' }}>
            {label}{' '}
            {required ? (
              <Span css={{ color: '$error', ml: '$space$6' }}>*</Span>
            ) : null}
          </Text>
        )}
        <Input
          ref={ref}
          multiline={textArea}
          id={name}
          name={name}
          type={inputType}
          className={`${showIcon ? 'iconItem' : ''}`}
          placeholder={placeholder}
          value={value}
          error={!!error}
          onChange={handleChange}
          disabled={disabled}
          minRows={textAreaElement?.row}
          startAdornment={
            <>
              {inputButton?.start ? (
                <Flex className="iconWrap" css={{ ...inputButton?.css }}>
                  {inputButton?.start}
                </Flex>
              ) : null}
              {iconPlacement === 'start' && showIcon ? (
                <Flex className="iconWrap">
                  {iconAsButton ? (
                    <Button
                      type="button"
                      onClick={toggleText}
                      transparent="asIconGray"
                      css={{
                        svg: {
                          path: {
                            stroke: '$gray400',
                          },
                        },
                      }}
                    >
                      {currentIcon}
                    </Button>
                  ) : (
                    currentIcon
                  )}
                </Flex>
              ) : null}
            </>
          }
          endAdornment={
            <>
              {buttonEnabled && <Button type="button">{buttonName}</Button>}

              {inputButton?.end ? (
                <Flex className="iconWrap" css={{ ...inputButton?.css }}>
                  {inputButton?.end}
                </Flex>
              ) : null}

              {iconPlacement === 'end' && showIcon ? (
                <Flex className="iconWrap">
                  {iconAsButton ? (
                    <Button
                      type="button"
                      onClick={toggleText}
                      transparent="asIconGray"
                      css={{
                        svg: {
                          path: {
                            stroke: '$gray400',
                          },
                        },
                      }}
                    >
                      {currentIcon}
                    </Button>
                  ) : (
                    currentIcon
                  )}
                </Flex>
              ) : null}
            </>
          }
        />
        {!!error && (
          <Text color="error" className="errorField">
            {error as ReactNode}
          </Text>
        )}
        {helperText ? (
          <Text as="p" css={{ mt: '$space$6', ...helperText?.css }}>
            {helperText?.text}
            {maxLength !== undefined &&
              ` ${maxLength - (text ? text.length : 0)} characters`}
          </Text>
        ) : null}
      </InputWrap>
    )
  }
)

export const FormWrap = styled('form', {
  display: 'flex',
  flexDirection: 'column',
})

export const InputBoxStyle = {
  background: '$inputBg',
  borderRadius: '$radii$sm',
  border: '1px solid',
  borderColor: '$inputBorder',
  outline: 'none',
}

const PrelabelTextCss = {
  '& + .MuiFormControl-root': {
    'ml': -1,
    'flexGrow': 1,
    '.MuiInputBase-root': {
      borderTopLeftRadius: 0,
      borderBottomLeftRadius: 0,
    },
  },
  '.Mui-focused': {
    zIndex: 1,
  },
  '.MuiInputBase-root': {
    borderTopRightRadius: 0,
    borderBottomRightRadius: 0,
  },
}

const InputWrapCSS = (css?: CSS | undefined) => ({
  '&.Mui': {
    '&FormControl-root': {
      marginBottom: '$space$20',
      width: '100%',
      ...(css ?? {}),
    },
  },
})

export const InputWrap = styled(FormControl, {
  '&.Mui': {
    '&FormControl-root': {
      mb: '$space$20',
      width: '100%',
      textarea: {
        fontFamily: '$Inter',
      },
    },
  },
  '.Mui': {
    '&InputBase': {
      '&-root': {
        ...InputBoxStyle,
        transition: 'border .6s $transition$easeOutQuint',
        p: 0,
        flexGrow: 1,
      },
      '&-adornedEnd': {
        '&.iconItem': {
          input: {
            'pr': 0,
            '& ~ .iconWrap': {
              ml: '$space$12',
              pr: '$space$14',
              mr: 0,
              pl: 0,
            },
          },
        },
      },
      '&-adornedStart': {
        '.iconWrap': {
          'mr': '$space$12',
          'pl': '$space$14',
          '& ~ input': {
            pl: 0,
            pr: '$space$14',
          },
        },
      },
    },
    '&-focused': {
      borderColor: '$inputBorderFocus',
      //boxShadow: '$inputShadow',
      svg: {
        path: {
          stroke: '$gray400',
        },
      },
    },
    '&-error': {
      'borderColor': '$inputErrorBorder',
      'boxShadow': '$inputErrorShadow',
      'svg': {
        path: {
          stroke: '$error500',
        },
      },
      '&:focus': {
        borderColor: '$inputErrorBorder',
      },
    },
  },
})

export const Input = styled(InputBase, {
  'height': 'auto',
  '.Mui': {
    '&InputBase': {
      '&-input': {
        'fontSize': '$16',
        'fontWeight': '$normal',
        'lineHeight': '$lineHeight$24',
        'color': '$inputText',
        'p': '$size$10 $size$14',
        'minHeight': '$size$24',
        '&::placeholder': {
          color: '$placeholder',
          fontWeight: '$normal',
        },

        '&:disabled': {
          'cursor': 'not-allowed',
          'backgroundColor': '$disabledBg',
          'color': '$disabledText',
          'borderColor': '$inputBorder',
          'borderRadius': '$radii$sm',
          '&::placeholder': {
            color: '$disabledText',
          },
          'svg': {
            path: {
              stroke: '$gray400',
            },
          },
        },
      },
    },
  },
})
